// Pricing workspace data layer — talks to the live /api/v1/pricing endpoints.
// The price book is the single source of truth; nothing hardcodes a price.
import { apiRequest } from '@/lib/supabase';

export interface TierDef {
  tier: string; name: string; base: number;
  vendorIncluded: number; vendorSeat: number;
  orgIncluded: number; orgSeat: number; overage: number; custom: boolean;
}
export interface ModuleDef { key: string; name: string; price: number; }
export interface PackDef { type: string; name: string; qty: number; price: number; }
export interface Band { from: number; to: number; price: number; }
export interface TermDef { years: number; discount: number; }
export interface Currency { sym: string; rate: number; }

export interface PriceBook {
  version: string;
  effective: string;
  baseCurrency: string;
  prevVersion?: string;
  tiers: TierDef[];
  modules: ModuleDef[];
  packs: PackDef[];
  discountFloorPct: number;
  vendorBands: Band[];
  orgBands: Band[];
  defaultTerm: number;
  terms: TermDef[];
  wholesaleDiscountPct: number;
  currencies: Record<string, Currency>;
}

export interface PriceBookVersionRow {
  version: string;
  effective_date: string;
  status: string;
  base_currency?: string;
  created_by?: string | null;
  published_at?: string | null;
  current?: boolean;
}

export interface ComputeRequest {
  tier: string;
  vendorPool?: number;
  orgSeats?: number;
  modules?: string[];
  packs?: Array<{ type: string; n: number }>;
  scope?: 'org' | 'platform';
  years?: number;
  currency?: string;
  quotedPrice?: number;
}
export interface ComputeResult {
  priceVersion: string;
  currency: string;
  list: {
    base: number; vendorSeatMrr: number; orgSeatMrr: number; moduleMrr: number; packMrr: number;
    retail: number; total: number; isPlatform: boolean; wholesalePct: number;
  };
  term: { monthly: number; annual: number; annualNet: number; tcv: number; discount: number };
  listInCurrency: number;
  crossCheck: { verdict: 'on-list' | 'off-list' | 'off-list-senior'; variancePct: number; offList: boolean; senior: boolean } | null;
}

export interface PriceBookFull extends PriceBook {
  status?: string;
  published_at?: string | null;
}

export const getPriceBook = () => apiRequest<PriceBook>('/pricing/price-book');
export const getPriceBookVersions = () => apiRequest<PriceBookVersionRow[]>('/pricing/price-book/versions');
export const getPriceBookVersion = (version: string) => apiRequest<PriceBookFull>(`/pricing/price-book/${version}`);
export const createPriceBookDraft = (version: string, effectiveDate?: string) =>
  apiRequest('/pricing/price-book', { method: 'POST', body: JSON.stringify({ version, effective_date: effectiveDate }) });
export const patchPriceBookDraft = (version: string, patch: Record<string, unknown>, effectiveDate?: string) =>
  apiRequest(`/pricing/price-book/${version}`, { method: 'PATCH', body: JSON.stringify({ patch, effective_date: effectiveDate }) });
export const publishPriceBook = (version: string) =>
  apiRequest(`/pricing/price-book/${version}/publish`, { method: 'POST', body: JSON.stringify({}) });
export const computePrice = (req: ComputeRequest) =>
  apiRequest<ComputeResult>('/pricing/compute', { method: 'POST', body: JSON.stringify(req) });

// Currency formatting helper bound to a price book's currency table.
export function makeFmt(currencies: Record<string, Currency>) {
  return (amountUsd: number, cur = 'USD', dp = 0): string => {
    const c = currencies[cur] || currencies.USD || { sym: '$', rate: 1 };
    return c.sym + (amountUsd * c.rate).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  };
}
